import { Chip, Flex, Group, Loader, Modal, Stack, Text } from '@mantine/core';
import { Dropzone } from '@mantine/dropzone';
import { IconMoodHappy, IconUpload, IconX } from '@tabler/icons-react';
import React, { useMemo } from 'react';
import type { Dispatch, MutableRefObject, SetStateAction } from 'react';
import { useTranslation } from '@refinedev/core';

import { TableActionButton } from 'components/common/TableActionButton';
import { useCurrentSiteStore } from 'store/SiteStore';
import { useCanModify } from 'hooks/useCanModify';
import type { RemittanceType } from 'api/action';

interface RemittanceToolBarProps {
  openModal: boolean;
  setOpenModal: Dispatch<SetStateAction<boolean>>;

  openRef: MutableRefObject<() => void>;
  remittanceFile: File | undefined;
  setRemittanceFile: Dispatch<SetStateAction<File | undefined>>;
  handleSubmitFiles: () => void;
  p4File: File | undefined;
  setP4File: Dispatch<SetStateAction<File | undefined>>;
  returnsFile: File | undefined;
  setReturnsFile: Dispatch<SetStateAction<File | undefined>>;
  payor: RemittanceType;
  setPayor: Dispatch<SetStateAction<RemittanceType>>;
  isUploading: boolean;
}

export const RemittanceToolBar: React.FC<RemittanceToolBarProps> = ({
  openModal,
  setOpenModal,
  openRef,
  remittanceFile,
  setRemittanceFile,
  handleSubmitFiles,
  p4File,
  setP4File,
  returnsFile,
  setReturnsFile,
  payor,
  setPayor,
  isUploading,
}) => {
  const { translate } = useTranslation();
  const isBCPayor = payor === 'BC';
  const isMBPayor = payor === 'MB';
  const isSKPayor = payor === 'SK';
  const { autoRemit } = useCurrentSiteStore();
  const { canModify } = useCanModify('remittances');
  const payorOptions: Array<{
    value: RemittanceType;
    label: string;
    disabled?: boolean;
  }> = [
    { value: 'BC', label: translate('pages:remittances.upload.payors.BC') },
    {
      value: 'MB',
      label: translate('pages:remittances.upload.payors.MB'),
      disabled: autoRemit,
    },
    { value: 'MSH', label: translate('pages:remittances.upload.payors.MSH') },
    { value: 'QDOC', label: translate('pages:remittances.upload.payors.QDOC') },
    { value: 'WCB', label: translate('pages:remittances.upload.payors.WCB') },
    { value: 'SK', label: translate('pages:remittances.upload.payors.SK') },
  ];

  const canSubmit = useMemo(() => {
    if (isBCPayor) return Boolean(remittanceFile);
    if (isMBPayor) return Boolean(remittanceFile && p4File);
    if (isSKPayor) return Boolean(returnsFile);
    return Boolean(p4File);
  }, [isBCPayor, isMBPayor, isSKPayor, remittanceFile, p4File, returnsFile]);

  const handlePayorChange = (value: RemittanceType) => {
    setPayor(value);
    setRemittanceFile(undefined);
    setP4File(undefined);
    setReturnsFile(undefined);
  };

  const renderDropzone = ({
    title,
    file,
    onDrop,
    accept,
  }: {
    title: string;
    file: File | undefined;
    onDrop: (file: File) => void;
    accept?: string[];
  }) => (
    <Dropzone
      openRef={openRef}
      onDrop={(files) => {
        const [file] = files;
        if (file) {
          onDrop(file);
        }
      }}
      activateOnClick={true}
      accept={accept}
    >
      <Text>{title}</Text>
      <Group
        style={{
          minHeight: 220,
          minWidth: 220,
          pointerEvents: 'none',
        }}
      >
        <Dropzone.Accept>
          <IconMoodHappy size="3.2rem" stroke={1.5} />
        </Dropzone.Accept>
        <Dropzone.Reject>
          <IconX size="3.2rem" stroke={1.5} />
        </Dropzone.Reject>
        <Dropzone.Idle>
          <IconUpload size="3.2rem" stroke={1.5} />
        </Dropzone.Idle>
        <div>
          {file && (
            <Text size="md" inline ta="center">
              {file.name}
            </Text>
          )}
        </div>
      </Group>
    </Dropzone>
  );

  return (
    <>
      <TableActionButton
        onClick={() => setOpenModal(true)}
        leftIcon={<IconUpload />}
        dataTestId="upload-remittance-button"
        disabled={!canModify}
      >
        {translate('pages:remittances.upload.open')}
      </TableActionButton>
      <Modal
        opened={openModal}
        onClose={() => {
          setRemittanceFile(undefined);
          setP4File(undefined);
          setReturnsFile(undefined);
          setOpenModal(false);
        }}
        title={translate('pages:remittances.upload.title')}
        size="lg"
        data-testid="upload-remittance-modal"
      >
        {isUploading ? (
          <Flex justify="center">
            <Loader size={'lg'} />
          </Flex>
        ) : (
          <>
            <Stack justify="center" style={{ width: '100%' }}>
              <Flex
                direction={'column'}
                justify={'center'}
                align={'center'}
                gap={'lg'}
                pt={'1rem'}
                pb={'2rem'}
              >
                <Text>{translate('pages:remittances.upload.payor')}</Text>
                <Flex
                  direction={'row'}
                  wrap="wrap"
                  justify={'center'}
                  align={'center'}
                  gap={'lg'}
                  pt={'1rem'}
                >
                  <Chip.Group value={payor}>
                    {payorOptions.map(({ value, label, disabled }) => (
                      <Chip
                        key={value}
                        color="blue"
                        variant="light"
                        value={value}
                        disabled={disabled}
                        onChange={() => handlePayorChange(value)}
                      >
                        {label}
                      </Chip>
                    ))}
                  </Chip.Group>
                </Flex>
              </Flex>
              <Flex w={'100%'} gap={'lg'} justify={'center'} align={'center'}>
                {isBCPayor &&
                  renderDropzone({
                    title: translate('pages:remittances.upload.bcFile'),
                    file: remittanceFile,
                    onDrop: setRemittanceFile,
                    accept: ['text/plain'],
                  })}
                {isMBPayor && (
                  <>
                    {renderDropzone({
                      title: translate(
                        'pages:remittances.upload.remittanceFile'
                      ),
                      file: remittanceFile,
                      onDrop: setRemittanceFile,
                      accept: ['text/plain'],
                    })}
                    {renderDropzone({
                      title: translate('pages:remittances.upload.incomeFile'),
                      file: p4File,
                      onDrop: setP4File,
                      accept: ['text/plain'],
                    })}
                  </>
                )}
                {isSKPayor &&
                  renderDropzone({
                    title: translate('pages:remittances.upload.returnFile'),
                    file: returnsFile,
                    onDrop: setReturnsFile,
                    accept: ['text/plain'],
                  })}
                {!isBCPayor &&
                  !isMBPayor &&
                  !isSKPayor &&
                  renderDropzone({
                    title: translate('pages:remittances.upload.incomeFile'),
                    file: p4File,
                    onDrop: setP4File,
                    accept: ['text/csv'],
                  })}
              </Flex>
            </Stack>
            <Group justify="center" mt="md">
              <TableActionButton
                onClick={() => handleSubmitFiles()}
                disabled={!canSubmit}
                dataTestId="submit-remittance-button"
                leftIcon={<IconUpload />}
              >
                {translate('pages:remittances.upload.submit')}
              </TableActionButton>
            </Group>
          </>
        )}
      </Modal>
    </>
  );
};
